import { USER_ROLES } from "@/utils/constants";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { message } from "antd";

/**
 * Maximum file size for images (2MB)
 */
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes

/**
 * Allowed image formats
 */
const ALLOWED_FORMATS = ["image/jpeg", "image/jpg", "image/png"];

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatDate = (dateString: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, options);
};

export const formatCurrency = (amount: number, currency: string = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(amount);
};

/**
 * Gets the display name for a user role
 * @param role - User role key
 * @returns Display name for the role
 */
export const getRoleDisplayName = (role: string): string => {
  return USER_ROLES.find((r) => r.value === role)?.label || "User";
};

/**
 * Determines if a color is light or dark using luminance calculation
 * @param hexColor - Hex color string (e.g., "#ffffff" or "ffffff")
 * @returns 'black' if the color is light, 'white' if the color is dark
 */
export const getContrastTextColor = (hexColor: string): "black" | "white" => {
  // Remove # if present
  const hex = hexColor.replace("#", "");

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance using WCAG formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black text for light backgrounds, white text for dark backgrounds
  return luminance > 0.5 ? "black" : "white";
};

export function generateMidLightColor(): string {
  const min = 90; // avoid dark colors
  const max = 200; // avoid very bright colors

  const randomChannel = () => Math.floor(Math.random() * (max - min + 1)) + min;

  const r = randomChannel();
  const g = randomChannel();
  const b = randomChannel();

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
}

/**
 * Validates file size and format for image uploads
 * @param file - File to validate
 * @param fileType - Type of file for error messages (e.g., "Logo", "Favicon")
 * @returns true if file is valid, false otherwise
 */
export const validateFile = (file: File, fileType: string): boolean => {
  if (file.size > MAX_FILE_SIZE) {
    message.error(`${fileType} size must be less than 2MB`);
    return false;
  }
  if (!ALLOWED_FORMATS.includes(file.type)) {
    message.error("Only JPG, JPEG, and PNG formats are allowed");
    return false;
  }
  return true;
};

// Helper function to check if rating date is current month/year
export const isCurrentMonthYear = (dateString: string): boolean => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const now = new Date();
  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
};

/**
 * Checks if the current browser hostname matches the company domain.
 * Supports direct inclusion (e.g. portal.company.com includes company.com)
 * and subdomain matching (e.g. one.platform.com vs one.com).
 * @param browserHost - Current browser hostname (window.location.hostname)
 * @param companyDomain - Company domain from database
 */
export const checkDomainMatch = (browserHost: string, companyDomain: string): boolean => {
  if (!browserHost || !companyDomain) return false;

  const host = browserHost.toLowerCase();
  const domain = companyDomain.toLowerCase();

  // Direct inclusion check (e.g., portal.apple.com containing apple.com)
  if (host.includes(domain)) return true;

  // Subdomain matching check (e.g., apple.google.com matching apple.com)
  const hostParts = host.split(".");
  const domainParts = domain.split(".");

  // Ensure we have enough parts to compare subdomains
  if (hostParts.length > 0 && domainParts.length > 0) {
    const hostSubdomain = hostParts[0];
    const domainSLD = domainParts[0];
    return hostSubdomain === domainSLD;
  }

  return false;
};
